import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

/* ───────────────────── constants ───────────────────── */
const TABS       = ['Contents', 'Logistics', 'Accounting', 'Attachments'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'JPY', 'GBP'];
const STATUSES   = ['Open', 'In Progress', 'Completed', 'Cancelled'];
const DEFECT_TYPES = ['', 'Material Defect', 'Machining Error', 'Welding Defect', 'Dimension Out', 'Surface Finish', 'Other'];

const STATUS_COLORS = {
  Open:          'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Completed:     'bg-green-100 text-green-700',
  Cancelled:     'bg-red-100 text-red-600',
};

const today = () => new Date().toISOString().slice(0, 10);

const blankForm = {
  rework_number: '', vendor_id: '', vendor_code: '', vendor_name: '',
  contact_person: '', vendor_ref_no: '', currency: 'INR',
  status: 'Open',
  posting_date: today(), due_date: '', document_date: today(),
  prod_order_id: '', prod_order_no: '',
  item_id: '', item_no: '', item_desc: '',
  rework_qty: '1', uom_name: 'NOS', unit_price: '0.0000',
  discount_pct: '0.00', tax_code: '',
  quality_warehouse_id: '', quality_wh_code: '',
  defect_type: '', reason: '', rework_cost: '0.0000',
  grpo_number: '',
  freight: '0.0000', total_amount: '0.0000',
  buyer: '', owner_field: '', remarks: '',
  ship_from: '', place_of_supply: '', branch: 'Arm Welders Pvt.Ltd.',
};

const fmtCur = (v) => Number(v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
export default function ReworkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm]             = useState({ ...blankForm });
  const [activeTab, setActiveTab]   = useState(0);
  const [loading, setLoading]       = useState(!isNew);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  /* lookup state */
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearch, setVendorSearch]   = useState('');
  const [showVendorDD, setShowVendorDD]   = useState(false);
  const [itemResults, setItemResults]     = useState([]);
  const [itemSearch, setItemSearch]       = useState('');
  const [showItemDD, setShowItemDD]       = useState(false);
  const [warehouses, setWarehouses]       = useState([]);

  /* load warehouses */
  useEffect(() => {
    api.get('/rework/warehouses').then(setWarehouses).catch(() => {});
  }, []);

  /* load existing rework or get next number */
  useEffect(() => {
    if (isNew) {
      api.get('/rework/next-number')
        .then(d => setForm(f => ({ ...f, rework_number: d.next })))
        .catch(() => {});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await api.get(`/rework/${id}`);
        if (cancelled) return;
        const h = { ...blankForm };
        for (const k of Object.keys(blankForm)) {
          if (data[k] !== undefined && data[k] !== null) {
            if (k === 'posting_date' || k === 'due_date' || k === 'document_date') {
              h[k] = data[k] ? String(data[k]).slice(0, 10) : '';
            } else {
              h[k] = data[k];
            }
          }
        }
        h._rework_id = data.rework_id;
        h.vendor_code = data.vendor_code || '';
        h.vendor_name = data.vendor_name || '';
        h.item_desc   = data.item_desc || '';
        h.prod_order_no = data.prod_order_no || '';
        h.quality_wh_code = data.quality_wh_code || '';
        setForm(h);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  /* field helper */
  const set = useCallback((field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    setError(''); setSuccessMsg('');
  }, []);

  /* vendor search */
  useEffect(() => {
    if (!vendorSearch.trim()) { setVendorResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/rework/vendors?search=${encodeURIComponent(vendorSearch)}`)
        .then(setVendorResults).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [vendorSearch]);

  const pickVendor = (v) => {
    setForm(f => ({
      ...f,
      vendor_id: v.vendor_id, vendor_code: v.vendor_code,
      vendor_name: v.vendor_name,
      contact_person: v.contact_person || f.contact_person,
      currency: v.currency || f.currency,
    }));
    setVendorSearch(''); setShowVendorDD(false);
  };

  /* item search */
  useEffect(() => {
    if (!itemSearch.trim()) { setItemResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/rework/items?search=${encodeURIComponent(itemSearch)}`)
        .then(setItemResults).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [itemSearch]);

  const pickItem = (item) => {
    setForm(f => ({
      ...f,
      item_id: item.item_id, item_no: item.item_no,
      item_desc: item.description,
      unit_price: String(item.unit_price || 0),
      uom_name: item.uom_name || 'NOS',
    }));
    setItemSearch(''); setShowItemDD(false);
  };

  /* computed totals */
  const { lineTotal, discountAmt, totalBeforeDisc, totalPayment } = useMemo(() => {
    const qty   = Number(form.rework_qty) || 0;
    const price = Number(form.unit_price) || 0;
    const disc  = Number(form.discount_pct) || 0;
    const gross = qty * price;
    const dAmt  = gross * disc / 100;
    const net   = gross - dAmt;
    const fr    = Number(form.freight) || 0;
    return {
      lineTotal: net,
      discountAmt: dAmt,
      totalBeforeDisc: gross,
      totalPayment: net + fr,
    };
  }, [form.rework_qty, form.unit_price, form.discount_pct, form.freight]);

  /* save */
  const handleSave = async () => {
    if (!form.item_id) return setError('Please select an item');
    if (!form.rework_number) return setError('Rework Number is required');
    if (!form.rework_qty || Number(form.rework_qty) <= 0) return setError('Quantity must be > 0');

    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = {};
      for (const k of Object.keys(blankForm)) {
        if (k === 'vendor_code' || k === 'vendor_name' || k === 'item_desc' ||
            k === 'prod_order_no' || k === 'quality_wh_code') continue;
        payload[k] = form[k];
      }
      payload.total_amount = totalPayment;
      payload.rework_cost  = form.rework_cost || totalPayment;

      if (isNew) {
        const created = await api.post('/rework', payload);
        setSuccessMsg('Rework order created');
        navigate(`/rework/${created.rework_id}`, { replace: true });
      } else {
        await api.put(`/rework/${form._rework_id}`, payload);
        setSuccessMsg('Rework order updated');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sap-blue border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-xs text-sap-textLight">Loading…</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* ═══ Title bar ═══ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/production/rework')}
            className="text-sap-textLight hover:text-sap-text text-xs">← Back</button>
          <h1 className="page-title">
            Goods Receipt PO {isNew ? '– New' : `– ${form.rework_number}`}
          </h1>
          {!isNew && (
            <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[form.status] || ''}`}>
              {form.status}
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark disabled:opacity-50 transition-colors font-semibold">
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
          <button onClick={() => navigate('/production/rework')}
            className="px-3 py-1 text-xs border border-sap-border rounded-lg hover:bg-sap-blue/5 transition-colors">
            Cancel
          </button>
        </div>
      </div>

      {/* ═══ Messages ═══ */}
      {error && <div className="px-3 py-2 text-xs rounded-lg bg-red-50 text-red-700 border border-red-200">{error}</div>}
      {successMsg && <div className="px-3 py-2 text-xs rounded-lg bg-green-50 text-green-700 border border-green-200">{successMsg}</div>}

      {/* ═══ Header section (matching SAP GRPO layout) ═══ */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-2 gap-x-10 gap-y-2">
          {/* ── Left column: Vendor info ── */}
          <div className="space-y-2">
            {/* Vendor searchable */}
            <div className="flex items-center gap-2 relative">
              <label className="w-32 text-xs text-sap-textLight text-right shrink-0">Vendor</label>
              <div className="flex-1 relative">
                {form.vendor_code ? (
                  <div className="flex items-center h-7 px-2 text-xs border border-sap-border rounded bg-gray-50">
                    <span className="font-medium text-sap-blue mr-2">{form.vendor_code}</span>
                    <span className="text-sap-textLight flex-1 truncate">{form.vendor_name}</span>
                    <button onClick={() => { set('vendor_id', ''); set('vendor_code', ''); set('vendor_name', ''); }}
                      className="ml-1 text-red-400 hover:text-red-600">✕</button>
                  </div>
                ) : (
                  <input value={vendorSearch} placeholder="Search vendor…"
                    onChange={e => { setVendorSearch(e.target.value); setShowVendorDD(true); }}
                    onFocus={() => setShowVendorDD(true)}
                    onBlur={() => setTimeout(() => setShowVendorDD(false), 200)}
                    className="w-full h-7 px-2 text-xs border border-sap-border rounded
                               focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20" />
                )}
                {showVendorDD && vendorResults.length > 0 && (
                  <div className="absolute z-20 top-8 left-0 right-0 bg-white border border-sap-border rounded shadow-lg max-h-48 overflow-auto">
                    {vendorResults.map(v => (
                      <button key={v.vendor_id} onMouseDown={() => pickVendor(v)}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-sap-blueLight transition-colors border-b border-gray-100 last:border-0">
                        <span className="font-medium text-sap-blue">{v.vendor_code}</span>
                        <span className="ml-2 text-sap-text">{v.vendor_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Field label="Name" value={form.vendor_name} disabled labelW="w-32" />
            <Field label="Contact Person" value={form.contact_person} onChange={v => set('contact_person', v)} labelW="w-32" />
            <Field label="Vendor Ref. No." value={form.vendor_ref_no} onChange={v => set('vendor_ref_no', v)} labelW="w-32" />
            <div className="flex items-center gap-2">
              <label className="w-32 text-xs text-sap-textLight text-right shrink-0">Local Currency</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)}
                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                           focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* ── Right column: Document info ── */}
          <div className="space-y-2">
            <Field label="No." value={form.rework_number} onChange={v => set('rework_number', v)} disabled={!isNew} labelW="w-32" />
            <div className="flex items-center gap-2">
              <label className="w-32 text-xs text-sap-textLight text-right shrink-0">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                           focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <Field label="Posting Date" value={form.posting_date} onChange={v => set('posting_date', v)} type="date" labelW="w-32" />
            <Field label="Due Date" value={form.due_date} onChange={v => set('due_date', v)} type="date" labelW="w-32" />
            <Field label="Document Date" value={form.document_date} onChange={v => set('document_date', v)} type="date" labelW="w-32" />
          </div>
        </div>
      </div>

      {/* ═══ Tab panel ═══ */}
      <div className="bg-white rounded-2xl shadow-sm">
        <div className="flex border-b border-sap-border/60 overflow-x-auto" role="tablist">
          {TABS.map((tab, i) => (
            <button key={tab} role="tab" onClick={() => setActiveTab(i)}
              className={`px-4 py-2 text-xs whitespace-nowrap border-b-2 transition-colors
                focus:outline-none focus:ring-1 focus:ring-sap-blue/30
                ${activeTab === i
                  ? 'border-sap-blue text-sap-blue font-semibold bg-white'
                  : 'border-transparent text-sap-textLight hover:text-sap-text hover:bg-sap-blue/5'}`}>
              {tab}
            </button>
          ))}
        </div>

        <div className="p-4 min-h-[360px]">
          {activeTab === 0 && (
            <ContentsTab form={form} set={set}
              itemSearch={itemSearch} setItemSearch={setItemSearch}
              itemResults={itemResults} showItemDD={showItemDD}
              setShowItemDD={setShowItemDD} pickItem={pickItem}
              warehouses={warehouses}
              lineTotal={lineTotal} totalBeforeDisc={totalBeforeDisc}
              discountAmt={discountAmt} totalPayment={totalPayment} />
          )}
          {activeTab === 1 && <LogisticsTab form={form} set={set} />}
          {activeTab === 2 && <AccountingTab form={form} set={set} />}
          {activeTab === 3 && <AttachmentsTab />}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tab 1 — Contents (Item grid + totals)
   ═══════════════════════════════════════════════════════════════ */
function ContentsTab({ form, set,
  itemSearch, setItemSearch, itemResults, showItemDD, setShowItemDD, pickItem,
  warehouses,
  lineTotal, totalBeforeDisc, discountAmt, totalPayment }) {

  return (
    <div className="space-y-3">
      {/* Item / Service Type row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-xs text-sap-textLight">Item/Service Type</label>
          <select className="h-7 px-2 text-xs border border-sap-border rounded bg-white" disabled>
            <option>Item</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-sap-textLight">Summary Type</label>
          <select className="h-7 px-2 text-xs border border-sap-border rounded bg-white" disabled>
            <option>No Summary</option>
          </select>
        </div>
      </div>

      {/* Line items grid (single row for rework) */}
      <div className="rounded-2xl shadow-sm overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-sap-text border-b border-sap-border sticky top-0 z-10">
            <tr>
              {['#', 'Item No.', 'Item Description', 'Quantity', 'UoM Name',
                'Unit Price', 'Discount %', 'Price after Disc.', 'Tax Code',
                'Total (LC)', 'Whse', 'Defect Type', 'GRPO No.'].map(h => (
                <th key={h} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white border-l-2 border-l-sap-blue/30">
              <td className="px-2 py-1 text-sap-textLight text-center w-8">1</td>

              {/* Item No. (searchable) */}
              <td className="px-2 py-1 relative w-36">
                {form.item_no ? (
                  <div className="flex items-center h-6 px-1 text-xs">
                    <span className="text-sap-blue font-medium truncate">{form.item_no}</span>
                    <button onClick={() => { set('item_id', ''); set('item_no', ''); set('item_desc', ''); }}
                      className="ml-1 text-red-400 hover:text-red-600 text-2xs shrink-0">✕</button>
                  </div>
                ) : (
                  <input value={itemSearch} placeholder="Search…"
                    onFocus={() => setShowItemDD(true)}
                    onChange={e => { setItemSearch(e.target.value); setShowItemDD(true); }}
                    onBlur={() => setTimeout(() => setShowItemDD(false), 200)}
                    className="w-full h-6 px-1 text-xs border border-sap-border rounded
                               focus:outline-none focus:border-sap-blue" />
                )}
                {showItemDD && itemResults.length > 0 && (
                  <div className="absolute z-30 top-8 left-1 w-72 bg-white border border-sap-border rounded shadow-lg max-h-40 overflow-auto">
                    {itemResults.map(it => (
                      <button key={it.item_id} onMouseDown={() => pickItem(it)}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-sap-blueLight border-b border-gray-100 last:border-0">
                        <span className="font-medium text-sap-blue">{it.item_no}</span>
                        <span className="ml-1.5">{it.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </td>

              <td className="px-2 py-1 text-sap-textLight max-w-[200px] truncate">{form.item_desc}</td>

              <td className="px-2 py-1 w-20">
                <input value={form.rework_qty} onChange={e => set('rework_qty', e.target.value)}
                  type="number" step="1" min="1"
                  className="w-full h-6 px-1 text-xs text-right border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>

              <td className="px-2 py-1 w-20">
                <input value={form.uom_name} onChange={e => set('uom_name', e.target.value)}
                  className="w-full h-6 px-1 text-xs border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>

              <td className="px-2 py-1 w-24">
                <input value={form.unit_price} onChange={e => set('unit_price', e.target.value)}
                  type="number" step="0.0001" min="0"
                  className="w-full h-6 px-1 text-xs text-right border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>

              <td className="px-2 py-1 w-16">
                <input value={form.discount_pct} onChange={e => set('discount_pct', e.target.value)}
                  type="number" step="0.01" min="0" max="100"
                  className="w-full h-6 px-1 text-xs text-right border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>

              <td className="px-2 py-1 text-right font-medium w-24">{fmtCur(lineTotal)}</td>

              <td className="px-2 py-1 w-16">
                <input value={form.tax_code || ''} onChange={e => set('tax_code', e.target.value)}
                  placeholder="V2"
                  className="w-full h-6 px-1 text-xs border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>

              <td className="px-2 py-1 text-right font-medium w-24">{fmtCur(lineTotal)}</td>

              <td className="px-2 py-1 w-24">
                <select value={form.quality_warehouse_id || ''} onChange={e => set('quality_warehouse_id', e.target.value)}
                  className="w-full h-6 px-1 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:border-sap-blue">
                  <option value="">—</option>
                  {warehouses.map(w => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code}</option>
                  ))}
                </select>
              </td>

              <td className="px-2 py-1 w-28">
                <select value={form.defect_type || ''} onChange={e => set('defect_type', e.target.value)}
                  className="w-full h-6 px-1 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:border-sap-blue">
                  {DEFECT_TYPES.map(d => <option key={d} value={d}>{d || '— None —'}</option>)}
                </select>
              </td>

              <td className="px-2 py-1 w-24">
                <input value={form.grpo_number || ''} onChange={e => set('grpo_number', e.target.value)}
                  className="w-full h-6 px-1 text-xs border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </td>
            </tr>
            {/* Empty row placeholder */}
            <tr className="bg-[#F0F4F3] border-l-2 border-l-sap-blue/10">
              <td className="px-2 py-3" colSpan={13}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bottom section: Buyer/Owner/Remarks (left) + Totals (right) */}
      <div className="flex justify-between items-end pt-2">
        {/* Left: Buyer, Owner, Remarks */}
        <div className="space-y-2 w-80">
          <Field label="Buyer" value={form.buyer || ''} onChange={v => set('buyer', v)} labelW="w-20" />
          <Field label="Owner" value={form.owner_field || ''} onChange={v => set('owner_field', v)} labelW="w-20" />
          <div className="flex items-start gap-2">
            <label className="w-20 text-xs text-sap-textLight text-right shrink-0 pt-1">Remarks</label>
            <textarea value={form.remarks || ''} onChange={e => set('remarks', e.target.value)}
              rows={2} className="flex-1 px-2 py-1 text-xs border border-sap-border rounded resize-y
                                  focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20" />
          </div>
        </div>

        {/* Right: Totals */}
        <div className="w-72 space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-sap-textLight">Total Before Discount</span>
            <span className="font-medium">{fmtCur(totalBeforeDisc)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sap-textLight">Discount</span>
            <div className="flex items-center gap-1">
              <input value={form.discount_pct} onChange={e => set('discount_pct', e.target.value)}
                type="number" step="0.01" min="0" max="100"
                className="w-14 h-5 px-1 text-xs text-right border border-sap-border rounded
                           focus:outline-none focus:border-sap-blue" />
              <span className="text-sap-textLight">%</span>
              <span className="ml-2 font-medium w-24 text-right">{fmtCur(discountAmt)}</span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sap-textLight">Freight</span>
            <input value={form.freight} onChange={e => set('freight', e.target.value)}
              type="number" step="0.01" min="0"
              className="w-28 h-5 px-1 text-xs text-right border border-sap-border rounded
                         focus:outline-none focus:border-sap-blue" />
          </div>
          <div className="flex justify-between">
            <span className="text-sap-textLight">Rounding</span>
            <span className="font-medium">₹0.00</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sap-textLight">Tax</span>
            <span className="font-medium">—</span>
          </div>
          <div className="flex justify-between border-t border-sap-border pt-1 font-semibold text-sm">
            <span>Total Payment Due</span>
            <span className="text-sap-blue">{fmtCur(totalPayment)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tab 2 — Logistics
   ═══════════════════════════════════════════════════════════════ */
function LogisticsTab({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-x-10 gap-y-2.5">
      <Sec text="Shipping" span={2} />
      <Field label="Ship From" value={form.ship_from || ''} onChange={v => set('ship_from', v)} />
      <Field label="Place of Supply" value={form.place_of_supply || ''} onChange={v => set('place_of_supply', v)} />
      <Field label="Branch" value={form.branch || ''} onChange={v => set('branch', v)} />
      <div />
      <Sec text="Production Linkage" span={2} />
      <Field label="Prod. Order ID" value={form.prod_order_id || ''} onChange={v => set('prod_order_id', v)} type="number" />
      <Field label="Prod. Order No" value={form.prod_order_no || ''} disabled />
      <Sec text="Rework Details" span={2} />
      <div className="col-span-2">
        <div className="flex items-start gap-2">
          <label className="w-40 text-xs text-sap-textLight text-right shrink-0 pt-1">Reason / Description</label>
          <textarea value={form.reason || ''} onChange={e => set('reason', e.target.value)}
            rows={4} className="flex-1 px-2 py-1 text-xs border border-sap-border rounded resize-y
                                focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20" />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tab 3 — Accounting
   ═══════════════════════════════════════════════════════════════ */
function AccountingTab({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-x-10 gap-y-2.5">
      <Sec text="Cost" span={2} />
      <Field label="Rework Cost" value={form.rework_cost} onChange={v => set('rework_cost', v)} type="number" step="0.01" />
      <Field label="Freight Charges" value={form.freight} onChange={v => set('freight', v)} type="number" step="0.01" />
      <Field label="Total Amount" value={fmtCur(Number(form.total_amount) || 0)} disabled />
      <div />
      <Sec text="References" span={2} />
      <Field label="GRPO Number" value={form.grpo_number || ''} onChange={v => set('grpo_number', v)} />
      <Field label="Tax Code" value={form.tax_code || ''} onChange={v => set('tax_code', v)} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tab 4 — Attachments
   ═══════════════════════════════════════════════════════════════ */
function AttachmentsTab() {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Sec text="Attachments" />
        <button className="px-2 py-1 text-xs bg-sap-blue text-white rounded-lg opacity-50 cursor-not-allowed font-semibold"
          disabled title="File upload not yet available">+ Attach File</button>
      </div>
      <p className="text-xs text-sap-textLight italic py-6 text-center">No attachments.</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Shared form primitives
   ═══════════════════════════════════════════════════════════════ */
function Field({ label, value, onChange, type = 'text', disabled = false, placeholder = '', step, labelW = 'w-40' }) {
  return (
    <div className="flex items-center gap-2">
      <label className={`${labelW} text-xs text-sap-textLight text-right shrink-0`}>{label}</label>
      <input type={type} value={value ?? ''} disabled={disabled} placeholder={placeholder} step={step}
        onChange={e => onChange && onChange(e.target.value)}
        className={`flex-1 h-7 px-2 text-xs border border-sap-border rounded
                    focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20
                    ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`} />
    </div>
  );
}

function Sec({ text, span = 1 }) {
  return (
    <div className={span > 1 ? `col-span-${span}` : ''}>
      <h3 className="text-xs font-semibold text-sap-text border-b border-sap-border pb-1 mb-1">{text}</h3>
    </div>
  );
}
